import { useCallback } from 'react';
import { App, Form } from 'antd';

import { useForm } from 'antd/es/form/Form';

import { useTranslation } from 'react-i18next';
import { CalendarDTO, CalendarInputName, useUpdateRegionalCalendarMutation } from 'entities/calendar';
import { UpdateRegionalCalendarFooter } from '../ui';

const useUpdateRegionalCalendar = (calendar: CalendarDTO): (() => void) => {
  const { t } = useTranslation();
  const { mutateAsync: updateRegionalCalendarMutate } = useUpdateRegionalCalendarMutation(calendar.id);

  const { modal } = App.useApp();
  const [form] = useForm<{ name: string }>();

  const openEditModal = useCallback((): void => {
    const instance = modal.confirm({
      title: t('Calendar:calendarsTable.createRegionalCalendar.title'),
      centered: true,
      closable: true,
      maskClosable: true,
      okButtonProps: { htmlType: 'submit' },
      cancelButtonProps: { htmlType: 'reset' },
      okText: t('Shared:saveBtn.text'),
      width: 450,
      icon: null,
      onOk: () => {
        return Promise.reject();
      },
      afterClose: () => {
        form.resetFields();
      },
      modalRender: (dom) => (
        <Form
          form={form}
          initialValues={{
            name: calendar.name,
          }}
          data-testid="update-regional-calendar-form"
          onFinish={({ name }) => {
            const trimmedName = name.trim();

            if (trimmedName === calendar.name) {
              form.setFields([
                {
                  name: 'name',
                  errors: [t('Calendar:calendarsTable.updateRegionalCalendar.validation.sameNameError')],
                },
              ]);
              return;
            }

            instance.update({ okButtonProps: { loading: true } });
            updateRegionalCalendarMutate(
              { id: calendar.id, name: trimmedName },
              {
                onSettled: () => instance.destroy(),
                onError: () => instance.update({ okButtonProps: { loading: false } }),
              },
            );
          }}
        >
          {dom}
        </Form>
      ),
      content: <CalendarInputName />,
      footer: (_, { CancelBtn, OkBtn }) => (
        <UpdateRegionalCalendarFooter calendar={calendar}>
          <CancelBtn />
          <OkBtn />
        </UpdateRegionalCalendarFooter>
      ),
    });
  }, [modal, t, form, updateRegionalCalendarMutate, calendar]);

  return openEditModal;
};

export { useUpdateRegionalCalendar };
